<template>
  <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-16">
        <!-- Left side -->
        <div class="flex items-center space-x-4">
          <!-- Sidebar toggle -->
          <button
            @click="$emit('toggleSidebar')"
            class="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 lg:hidden"
            aria-label="Toggle sidebar"
          >
            <Bars3Icon class="h-6 w-6" />
          </button>

          <!-- Desktop sidebar toggle -->
          <button
            @click="$emit('toggleSidebar')"
            class="hidden lg:flex p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label="Toggle sidebar"
          >
            <Bars3Icon v-if="!sidebarOpen" class="h-5 w-5" />
            <XMarkIcon v-else class="h-5 w-5" />
          </button>

          <!-- Search -->
          <div class="hidden md:block relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" />
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Buscar..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
              @keyup.enter="handleSearch"
            />
          </div>
        </div>

        <!-- Right side -->
        <div class="flex items-center space-x-4">
          <!-- Mobile search -->
          <button
            @click="showMobileSearch = !showMobileSearch"
            class="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label="Search"
          >
            <MagnifyingGlassIcon class="h-6 w-6" />
          </button>

          <!-- Notifications -->
          <div class="relative">
            <button
              @click="showNotifications = !showNotifications"
              class="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 relative"
              aria-label="Notifications"
            >
              <BellIcon class="h-6 w-6" />
              <span
                v-if="unreadNotifications > 0"
                class="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
              >
                {{ unreadNotifications > 99 ? '99+' : unreadNotifications }}
              </span>
            </button>

            <!-- Notifications dropdown -->
            <div
              v-if="showNotifications"
              v-click-outside="() => showNotifications = false"
              class="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
            >
              <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white">Notificações</h3>
                  <button
                    v-if="unreadNotifications > 0"
                    @click="markAllAsRead"
                    class="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    Marcar todas como lidas
                  </button>
                </div>
              </div>
              <div class="max-h-96 overflow-y-auto">
                <div
                  v-if="notifications.length === 0"
                  class="p-4 text-center text-gray-500 dark:text-gray-400"
                >
                  Nenhuma notificação
                </div>
                <div
                  v-for="notification in notifications"
                  :key="notification.id"
                  class="p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  :class="{ 'bg-blue-50 dark:bg-blue-900/20': !notification.read }"
                  @click="markAsRead(notification.id)"
                >
                  <div class="flex items-start space-x-3">
                    <div class="flex-shrink-0">
                      <div
                        class="h-8 w-8 rounded-full flex items-center justify-center"
                        :class="getNotificationIconClass(notification.type)"
                      >
                        <component :is="getNotificationIcon(notification.type)" class="h-4 w-4" />
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ notification.title }}
                      </p>
                      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {{ notification.message }}
                      </p>
                      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {{ formatDate(notification.createdAt) }}
                      </p>
                    </div>
                    <div v-if="!notification.read" class="flex-shrink-0">
                      <div class="h-2 w-2 bg-blue-500 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="p-4 border-t border-gray-200 dark:border-gray-700">
                <router-link
                  to="/communication/notifications"
                  class="block text-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  @click="showNotifications = false"
                >
                  Ver todas as notificações
                </router-link>
              </div>
            </div>
          </div>

          <!-- Theme toggle -->
          <button
            @click="toggleTheme"
            class="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          >
            <SunIcon v-if="isDark" class="h-6 w-6" />
            <MoonIcon v-else class="h-6 w-6" />
          </button>

          <!-- User menu -->
          <div class="relative">
            <button
              @click="showUserMenu = !showUserMenu"
              class="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              aria-label="User menu"
            >
              <div class="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <span class="text-primary-600 dark:text-primary-400 font-medium text-sm">
                  {{ userInitials }}
                </span>
              </div>
              <div class="hidden md:block text-left">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ authStore.user?.full_name || 'Usuário' }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ roleLabel }}
                </p>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400" />
            </button>

            <!-- User dropdown -->
            <div
              v-if="showUserMenu"
              v-click-outside="() => showUserMenu = false"
              class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
            >
              <div class="py-1">
                <router-link
                  to="/profile"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="showUserMenu = false"
                >
                  <div class="flex items-center space-x-2">
                    <UserCircleIcon class="h-4 w-4" />
                    <span>Meu Perfil</span>
                  </div>
                </router-link>
                <router-link
                  to="/settings"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="showUserMenu = false"
                >
                  <div class="flex items-center space-x-2">
                    <CogIcon class="h-4 w-4" />
                    <span>Configurações</span>
                  </div>
                </router-link>
                <div class="border-t border-gray-200 dark:border-gray-700"></div>
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <div class="flex items-center space-x-2">
                    <ArrowRightOnRectangleIcon class="h-4 w-4" />
                    <span>Sair</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile search -->
      <div
        v-if="showMobileSearch"
        class="md:hidden pb-4"
      >
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar..."
            class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white dark:bg-gray-700 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
            @keyup.enter="handleSearch"
          />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ChevronDownIcon,
  UserCircleIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline';

// Props
interface Props {
  sidebarOpen: boolean;
}

defineProps<Props>();

// Emits
defineEmits<{
  toggleSidebar: [];
}>();

// Stores
const authStore = useAuthStore();
const notificationStore = useNotificationsStore();
const router = useRouter();

// State
const searchQuery = ref('');
const showMobileSearch = ref(false);
const showNotifications = ref(false);
const showUserMenu = ref(false);
const isDark = ref(false);

// Computed
const userInitials = computed(() => {
  const name = authStore.user?.full_name || 'U';
  return name
    .split(' ')
    .map((word: string) => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

const roleLabel = computed(() => {
  const roleLabels = {
    admin: 'Administrador',
    director: 'Diretor',
    coordinator: 'Coordenador',
    teacher: 'Professor',
    student: 'Aluno',
    parent: 'Responsável',
    financial: 'Financeiro',
  };
  return roleLabels[authStore.user?.role as keyof typeof roleLabels] || 'Usuário';
});

const notifications = computed(() => notificationStore.notifications);
const unreadNotifications = computed(() => notificationStore.unreadCount);

// Methods
const toggleTheme = () => {
  const html = document.documentElement;
  const isDarkMode = html.classList.contains('dark');
  
  if (isDarkMode) {
    html.classList.remove('dark');
    localStorage.setItem('theme', 'light');
    isDark.value = false;
  } else {
    html.classList.add('dark');
    localStorage.setItem('theme', 'dark');
    isDark.value = true;
  }
};

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({
      name: 'Search',
      query: { q: searchQuery.value.trim() }
    });
    showMobileSearch.value = false;
  }
};

const handleLogout = async () => {
  try {
    await authStore.logout();
    router.push('/auth/login');
  } catch (error) {
    console.error('Erro ao fazer logout:', error);
  }
  showUserMenu.value = false;
};

const markAsRead = (notificationId: string) => {
  notificationStore.markAsRead(notificationId);
};

const markAllAsRead = () => {
  notificationStore.markAllAsRead();
};

const getNotificationIcon = (type: string) => {
  const icons = {
    info: InformationCircleIcon,
    warning: ExclamationTriangleIcon,
    success: CheckCircleIcon,
    error: XCircleIcon,
  };
  return icons[type as keyof typeof icons] || InformationCircleIcon;
};

const getNotificationIconClass = (type: string) => {
  const classes = {
    info: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
    warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400',
    success: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400',
    error: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400',
  };
  return classes[type as keyof typeof classes] || classes.info;
};

const formatDate = (date: string) => {
  const now = new Date();
  const notificationDate = new Date(date);
  const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Agora';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m atrás`;
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h atrás`;
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d atrás`;
  }
};

// Lifecycle
onMounted(() => {
  // Initialize theme
  isDark.value = document.documentElement.classList.contains('dark');
  
  // Load notifications
  notificationStore.fetchNotifications();
});
</script>

<style scoped>
/* Custom dropdown animations */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Focus styles for accessibility */
button:focus,
input:focus {
  outline: 2px solid rgb(59 130 246);
  outline-offset: 2px;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .bg-white {
    border: 1px solid black;
  }
  
  .dark .bg-gray-800 {
    border: 1px solid white;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .transition-colors {
    transition: none;
  }
}
</style>